/**
 * Supabase-backed Automation & AutomationRun services.
 * Maintains a local in-memory cache for synchronous access while persisting to DB.
 * Legacy localStorage automation data is intentionally cleared and never re-imported.
 */

import type { AutomationFlow, AutomationRun } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { generateId } from './dataService';
import { getCurrentWorkspaceId } from './workspaceState';

// ===================== HELPERS =====================

type Listener = () => void;
const flowListeners: Listener[] = [];
const runListeners: Listener[] = [];

function notifyFlows() { flowListeners.forEach(fn => fn()); }
function notifyRuns() { runListeners.forEach(fn => fn()); }

// ===================== LOCAL CACHE =====================

let flowCache: AutomationFlow[] = [];
let runCache: AutomationRun[] = [];
let flowsLoaded = false;
let runsLoaded = false;

const LEGACY_STORAGE_KEYS = [
  'wf_automations',
  'wf_automation_runs',
  'wf_automations_migrated',
  'wf_automation_triggers_cache',
] as const;

function clearLegacyAutomationStorage() {
  try {
    for (const key of LEGACY_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore localStorage access issues
  }
}

// ===================== DB ↔ TYPE MAPPING =====================

interface DbFlow {
  id: string;
  name: string;
  description: string | null;
  status: string;
  trigger_type: string;
  version: number;
  nodes: any;
  edges: any;
  published_at: string | null;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

interface DbRun {
  id: string;
  automation_id: string;
  automation_name: string | null;
  contact_id: string | null;
  conversation_id: string | null;
  trigger_source: string;
  status: string;
  is_test: boolean;
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
  execution_log: any;
  created_at: string;
}

function dbFlowToLocal(db: DbFlow): AutomationFlow {
  return {
    id: db.id,
    name: db.name,
    description: db.description || undefined,
    status: db.status as AutomationFlow['status'],
    triggerType: db.trigger_type,
    version: db.version,
    nodes: Array.isArray(db.nodes) ? db.nodes : [],
    edges: Array.isArray(db.edges) ? db.edges : [],
    publishedAt: db.published_at || undefined,
    lastRunAt: db.last_run_at || undefined,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
  };
}

function localFlowToDb(flow: AutomationFlow): Record<string, any> {
  return {
    id: flow.id,
    name: flow.name,
    description: flow.description || null,
    status: flow.status,
    trigger_type: flow.triggerType,
    version: flow.version,
    nodes: flow.nodes,
    edges: flow.edges,
    published_at: flow.publishedAt || null,
    last_run_at: flow.lastRunAt || null,
    created_at: flow.createdAt,
    updated_at: flow.updatedAt,
    workspace_id: getCurrentWorkspaceId(),
  };
}

function dbRunToLocal(db: DbRun): AutomationRun {
  return {
    id: db.id,
    automationId: db.automation_id,
    automationName: db.automation_name || undefined,
    contactId: db.contact_id || '',
    conversationId: db.conversation_id || undefined,
    triggerSource: db.trigger_source,
    status: db.status as AutomationRun['status'],
    isTest: db.is_test,
    startedAt: db.started_at,
    finishedAt: db.finished_at || undefined,
    errorMessage: db.error_message || undefined,
    executionLog: Array.isArray(db.execution_log) ? db.execution_log : [],
    createdAt: db.created_at,
  };
}

function localRunToDb(run: AutomationRun): Record<string, any> {
  return {
    id: run.id,
    automation_id: run.automationId,
    automation_name: run.automationName || null,
    contact_id: run.contactId || null,
    conversation_id: run.conversationId || null,
    trigger_source: run.triggerSource,
    status: run.status,
    is_test: run.isTest,
    started_at: run.startedAt,
    finished_at: run.finishedAt || null,
    error_message: run.errorMessage || null,
    execution_log: run.executionLog,
    created_at: run.createdAt,
    workspace_id: getCurrentWorkspaceId(),
  };
}

// ===================== INITIAL LOAD =====================

async function loadFlows(): Promise<AutomationFlow[]> {
  clearLegacyAutomationStorage();
  try {
    const { data, error } = await supabase
      .from('automation_flows' as any)
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) throw error;

    flowCache = (data as any[] || []).map(dbFlowToLocal);
  } catch (e) {
    console.error('[AutomationDB] Failed to load flows:', e);
    flowCache = [];
  }

  flowsLoaded = true;
  return flowCache;
}

async function loadRuns(): Promise<AutomationRun[]> {
  clearLegacyAutomationStorage();
  try {
    const { data, error } = await supabase
      .from('automation_runs' as any)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) throw error;

    runCache = (data as any[] || []).map(dbRunToLocal);
  } catch (e) {
    console.error('[AutomationDB] Failed to load runs:', e);
    runCache = [];
  }

  runsLoaded = true;
  return runCache;
}

// Eagerly load on import (best-effort — workspace may not be resolved yet;
// reloadAutomations() will be called again from App.tsx once workspace is ready)
let _initPromise: Promise<unknown> = Promise.all([loadFlows(), loadRuns()]).then(() => {
  notifyFlows();
  notifyRuns();
});

/** Wait for initial DB load to complete */
export function waitForInit() { return _initPromise; }

/**
 * Reload flows + runs from DB. Call this after the workspace_id has been
 * resolved (or after switching workspaces) so RLS-filtered queries return
 * the correct rows for the active workspace.
 */
export async function reloadAutomations(): Promise<void> {
  flowsLoaded = false;
  runsLoaded = false;
  flowCache = [];
  runCache = [];
  _initPromise = Promise.all([loadFlows(), loadRuns()]).then(() => {
    notifyFlows();
    notifyRuns();
  });
  await _initPromise;
}

// ===================== AUTOMATION FLOW SERVICE =====================

export const automationDbService = {
  subscribe: (callback: (automations: AutomationFlow[]) => void) => {
    const handler = () => callback([...flowCache].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
    handler();
    flowListeners.push(handler);
    return () => { const i = flowListeners.indexOf(handler); if (i >= 0) flowListeners.splice(i, 1); };
  },

  getAll: (): AutomationFlow[] => flowCache,

  getById: (id: string): AutomationFlow | null => flowCache.find(a => a.id === id) || null,

  getActive: (): AutomationFlow[] => flowCache.filter(a => a.status === 'active'),

  getByTrigger: (triggerType: string): AutomationFlow[] => flowCache.filter(a => a.status === 'active' && a.triggerType === triggerType),

  create: (data: Partial<AutomationFlow>): string => {
    const now = new Date().toISOString();
    const flow: AutomationFlow = {
      id: data.id || generateId(),
      name: data.name || 'New Automation',
      description: data.description,
      status: data.status || 'draft',
      triggerType: data.triggerType || 'new_contact_created',
      version: data.version || 1,
      nodes: data.nodes || [],
      edges: data.edges || [],
      publishedAt: data.publishedAt,
      lastRunAt: data.lastRunAt,
      createdAt: data.createdAt || now,
      updatedAt: now,
    };

    flowCache.push(flow);
    notifyFlows();

    supabase.from('automation_flows' as any).insert(localFlowToDb(flow) as any).then(({ error }) => {
      if (error) console.error('[AutomationDB] Insert flow error:', error.message);
    });

    return flow.id;
  },

  update: (id: string, data: Partial<AutomationFlow>) => {
    const idx = flowCache.findIndex(a => a.id === id);
    if (idx === -1) return;

    const updatedAt = new Date().toISOString();
    flowCache[idx] = { ...flowCache[idx], ...data, updatedAt };
    notifyFlows();

    const dbUpdate: Record<string, any> = { updated_at: updatedAt };
    if (data.name !== undefined) dbUpdate.name = data.name;
    if (data.description !== undefined) dbUpdate.description = data.description || null;
    if (data.status !== undefined) dbUpdate.status = data.status;
    if (data.triggerType !== undefined) dbUpdate.trigger_type = data.triggerType;
    if (data.version !== undefined) dbUpdate.version = data.version;
    if (data.nodes !== undefined) dbUpdate.nodes = data.nodes;
    if (data.edges !== undefined) dbUpdate.edges = data.edges;
    if (data.publishedAt !== undefined) dbUpdate.published_at = data.publishedAt || null;
    if (data.lastRunAt !== undefined) dbUpdate.last_run_at = data.lastRunAt || null;

    supabase.from('automation_flows' as any).update(dbUpdate as any).eq('id', id).then(({ error }) => {
      if (error) console.error('[AutomationDB] Update flow error:', error.message);
    });
  },

  delete: async (id: string): Promise<void> => {
    clearLegacyAutomationStorage();

    const results = await Promise.all([
      supabase.from('automation_trigger_runs' as any).delete().eq('automation_id', id),
      supabase.from('automation_runs' as any).delete().eq('automation_id', id),
      supabase.from('automation_triggers' as any).delete().eq('automation_id', id),
      supabase.from('automation_flows' as any).delete().eq('id', id),
    ]);

    const errors = results.flatMap(({ error }) => (error ? [error.message] : []));
    if (errors.length > 0) {
      throw new Error(errors.join(' | '));
    }

    flowCache = flowCache.filter(a => a.id !== id);
    runCache = runCache.filter(r => r.automationId !== id);
    notifyFlows();
    notifyRuns();
  },

  toggleStatus: (id: string, status: AutomationFlow['status']) => {
    automationDbService.update(id, { status, ...(status === 'active' ? { publishedAt: new Date().toISOString() } : {}) });
  },

  validate: (flow: AutomationFlow): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const triggerNodes = flow.nodes.filter(n => n.type === 'Trigger');
    if (triggerNodes.length === 0) errors.push('Must have at least one trigger node');
    if (flow.nodes.length < 2) errors.push('Must have at least one action node');
    if (!flow.name?.trim()) errors.push('Name is required');
    return { valid: errors.length === 0, errors };
  },
};

// ===================== AUTOMATION RUN SERVICE =====================

export const automationRunDbService = {
  subscribe: (callback: (runs: AutomationRun[]) => void) => {
    const handler = () => callback([...runCache].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    handler();
    runListeners.push(handler);
    return () => { const i = runListeners.indexOf(handler); if (i >= 0) runListeners.splice(i, 1); };
  },

  getAll: (): AutomationRun[] => runCache,

  getByAutomation: (automationId: string): AutomationRun[] => runCache.filter(r => r.automationId === automationId),

  getStats: (automationId: string): { triggered: number; completed: number; failed: number; successRate: number } => {
    const runs = automationRunDbService.getByAutomation(automationId);
    const triggered = runs.length;
    const completed = runs.filter(r => r.status === 'completed').length;
    const failed = runs.filter(r => r.status === 'failed').length;
    return { triggered, completed, failed, successRate: triggered > 0 ? (completed / triggered) * 100 : 0 };
  },

  create: (data: Partial<AutomationRun>): string => {
    const now = new Date().toISOString();
    const run: AutomationRun = {
      id: generateId(),
      automationId: data.automationId || '',
      automationName: data.automationName,
      contactId: data.contactId || '',
      conversationId: data.conversationId,
      triggerSource: data.triggerSource || 'manual',
      status: data.status || 'queued',
      isTest: data.isTest || false,
      startedAt: data.startedAt || now,
      finishedAt: data.finishedAt,
      errorMessage: data.errorMessage,
      executionLog: data.executionLog || [],
      createdAt: data.createdAt || now,
    };
    runCache.push(run);
    notifyRuns();
    supabase.from('automation_runs' as any).insert(localRunToDb(run) as any).then(({ error }) => {
      if (error) console.error('[AutomationDB] Insert run error:', error.message);
    });
    return run.id;
  },

  update: (id: string, data: Partial<AutomationRun>) => {
    const idx = runCache.findIndex(r => r.id === id);
    if (idx === -1) return;
    runCache[idx] = { ...runCache[idx], ...data };
    notifyRuns();
    const dbUpdate: Record<string, any> = {};
    if (data.status !== undefined) dbUpdate.status = data.status;
    if (data.finishedAt !== undefined) dbUpdate.finished_at = data.finishedAt || null;
    if (data.errorMessage !== undefined) dbUpdate.error_message = data.errorMessage || null;
    if (data.executionLog !== undefined) dbUpdate.execution_log = data.executionLog;
    if (data.automationName !== undefined) dbUpdate.automation_name = data.automationName || null;
    if (Object.keys(dbUpdate).length > 0) {
      supabase.from('automation_runs' as any).update(dbUpdate as any).eq('id', id).then(({ error }) => {
        if (error) console.error('[AutomationDB] Update run error:', error.message);
      });
    }
  },
};